import { useState } from 'react';
import { useNavigate, useLocation, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { getMyProfile } from '../lib/cortivoxApi';

// Куда после входа: у пациента свой кабинет, кабинет врача ему не показываем.
const homeOf = (profile) => (profile?.role === 'patient' ? '/patient' : '/doctor/dashboard');

// Единая страница входа/регистрации врача — на GoTrue (supabase.auth), а не на
// самодельной таблице с паролями в открытом виде. Регистрация заводит
// пользователя в общем пуле и создаёт профиль через cortivox-api. Вход
// опционален: остальные маршруты не заперты.
//
// Вход — по email, как его нативно поддерживает GoTrue. Вход по логину убран:
// колонки для логина нет ни в одной схеме, и он требовал отдельного
// неавторизованного эндпоинта для перевода логина в email.
export default function DoctorAuthPage() {
  const { signIn, signUp, user, doctor: profile, configured } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname;

  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [specialization, setSpecialization] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Уже вошли — уводим на исходную страницу.
  if (user) return <Navigate to={from || homeOf(profile)} replace />;

    const submit = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);
    try {
      if (mode === 'login') {
        const { error: err } = await signIn(email.trim(), password);
        if (err) throw new Error('Неверный email или пароль');
        navigate(from || homeOf(await getMyProfile().catch(() => null)), { replace: true });
        return;
      } else {
        const { error: err, warning } = await signUp(email.trim(), password, {
          role: 'doctor',
          fullName: fullName.trim(),
          specialization: specialization.trim(),
        });
        if (err) throw err;
        if (warning) {
          setNotice('Аккаунт создан. Профиль врача сохранить не удалось — обратитесь к администратору.');
        }
      }
      navigate(from || '/doctor/dashboard', { replace: true });
    } catch (err) {
      setError(err?.message || 'Не удалось выполнить запрос. Попробуйте ещё раз.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-background p-6">
      <form
        onSubmit={submit}
        className="max-w-sm w-full rounded-2xl border border-border bg-secondary/40 p-8 flex flex-col gap-4"
      >
        <Link to="/" className="text-center text-2xl font-bold text-primary">
          Cortivox
        </Link>
        <h1 className="text-lg font-semibold text-primary">
          {mode === 'login' ? 'Вход' : 'Регистрация врача'}
        </h1>

        {!configured && (
          <p className="text-sm text-amber-500">
            Авторизация не настроена в этой сборке. Обратитесь к администратору.
          </p>
        )}

        <input
          type="email"
          required
          autoComplete="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-primary"
        />

        <input
          type="password"
          required
          autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          placeholder="Пароль"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-primary"
        />

        {mode === 'register' && (
          <>
            <input
              type="text"
              placeholder="ФИО"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-primary"
            />

            <input
              type="text"
              placeholder="Специализация (напр. невролог)"
              value={specialization}
              onChange={(e) => setSpecialization(e.target.value)}
              className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-primary"
            />
          </>
        )}

        {error && <p className="text-xs text-red-600">{error}</p>}
        {notice && <p className="text-xs text-amber-600">{notice}</p>}

        <button
          type="submit"
          disabled={loading || !configured}
          className="rounded-xl bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold disabled:opacity-50"
        >
          {loading ? 'Подождите…' : mode === 'login' ? 'Войти' : 'Зарегистрироваться'}
        </button>

        <button
          type="button"
          onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); setNotice(''); }}
          className="text-xs text-muted-foreground underline"
        >
          {mode === 'login' ? 'Нет аккаунта? Зарегистрироваться' : 'Уже есть аккаунт? Войти'}
        </button>

        <Link to="/patient/register" className="text-center text-xs text-muted-foreground underline">
          Вы пациент? Зарегистрироваться
        </Link>

        <Link to="/" className="text-center text-xs text-muted-foreground hover:text-primary">
          Вернуться на главную
        </Link>
      </form>
    </main>
  );
}